import { Sport } from '../types';

// Tab configuration
export interface DepthChartTab {
  id: string;
  label: string;
  defaultValue?: string; // For optional dropdown values
}

// Depth string configuration (1st string, 2nd string, etc.)
export interface DepthStringConfig {
  defaultLabels: {id: number, name: string}[];
  sportSpecificName?: string; // e.g., "Lines" for hockey, "Subs" for soccer
}

// Formation configuration
export interface FormationConfig {
  enabled: boolean;
  label?: string;
  sportSpecificFormations?: boolean; // Whether to use sport-specific formations
  defaultFormation?: string;
}

// Print configuration
export interface PrintConfig {
  stylesheets: string[]; // Array of CSS file paths to load
  layout: 'standard' | 'horizontal' | 'combined'; // Layout type
  customLayoutFunction?: string; // Name of custom layout function if needed
}

// Sport-specific ruleset for depth chart
export interface SportRuleset {
  tabs: DepthChartTab[];
  depthStrings: DepthStringConfig;
  formations: FormationConfig;
  showSpecialTeams: boolean;
  specialTeamsUnits?: string[];
  // Additional sport-specific settings
  useInnings?: boolean;
  defaultInnings?: number;
  inningRanges?: {min: number; max: number}; // Min/max number of innings
  // Print configuration
  print: PrintConfig;
}

// Define rulesets for all sports
export const SPORT_RULESETS: Record<Sport, SportRuleset> = {
  Football: {
    tabs: [
      { id: 'Offense', label: 'Offense' },
      { id: 'Defense', label: 'Defense' },
      { id: 'Special Teams', label: 'Special Teams' }
    ],
    depthStrings: {
      defaultLabels: [
        {id: 1, name: '1st String'},
        {id: 2, name: '2nd String'},
        {id: 3, name: '3rd String'}
      ]
    },
    formations: {
      enabled: true,
      label: 'Formation',
      sportSpecificFormations: true,
      defaultFormation: 'Spread'
    },
    showSpecialTeams: true,
    specialTeamsUnits: ['Kickoff', 'Kick Return', 'Punt', 'Punt Return', 'Field Goal'],
    print: {
      stylesheets: ['/src/styles/print/base-print.css', '/src/styles/print/football-print.css'],
      layout: 'horizontal',
      customLayoutFunction: 'renderFootballLayout'
    }
  },
  
  FlagFootball: {
    tabs: [
      { id: 'Offense', label: 'Offense' },
      { id: 'Defense', label: 'Defense' }
    ],
    depthStrings: {
      defaultLabels: [
        {id: 1, name: '1st String'},
        {id: 2, name: '2nd String'},
        {id: 3, name: '3rd String'}
      ]
    },
    formations: {
      enabled: true,
      label: 'Formation',
      sportSpecificFormations: true,
      defaultFormation: 'Spread'
    },
    showSpecialTeams: false,
    print: {
      stylesheets: ['/src/styles/print/base-print.css'],
      layout: 'standard'
    }
  },
  
  Basketball: {
    tabs: [
      { id: 'Team', label: 'Team' }
    ],
    depthStrings: {
      defaultLabels: [
        {id: 1, name: 'Starters'},
        {id: 2, name: 'Rotation'},
        {id: 3, name: 'Bench'}
      ],
      sportSpecificName: 'Unit'
    },
    formations: {
      enabled: false
    },
    showSpecialTeams: false,
    print: {
      stylesheets: ['/src/styles/print/base-print.css'],
      layout: 'standard'
    }
  },
  
  Baseball: {
    tabs: [
      { id: 'Lineup', label: 'Lineup' },
      { id: 'Fielding', label: 'Fielding' }
    ],
    depthStrings: {
      defaultLabels: [
        {id: 1, name: 'Starters'},
        {id: 2, name: 'Backups'}
      ]
    },
    formations: {
      enabled: false
    },
    showSpecialTeams: false,
    useInnings: true,
    defaultInnings: 7,
    inningRanges: {min: 1, max: 9},
    print: {
      stylesheets: ['/src/styles/print/base-print.css', '/src/styles/print/baseball-print.css'],
      layout: 'combined',
      customLayoutFunction: 'renderBaseballLayout'
    }
  },
  
  Softball: {
    tabs: [
      { id: 'Lineup', label: 'Lineup' },
      { id: 'Fielding', label: 'Fielding' }
    ],
    depthStrings: {
      defaultLabels: [
        {id: 1, name: 'Starters'},
        {id: 2, name: 'Backups'}
      ]
    },
    formations: {
      enabled: false
    },
    showSpecialTeams: false,
    useInnings: true,
    defaultInnings: 7,
    inningRanges: {min: 1, max: 7},
    print: {
      stylesheets: ['/src/styles/print/base-print.css', '/src/styles/print/baseball-print.css'],
      layout: 'combined',
      customLayoutFunction: 'renderBaseballLayout'
    }
  },
  
  Soccer: {
    tabs: [
      { id: 'Team', label: 'Team' }
    ],
    depthStrings: {
      defaultLabels: [
        {id: 1, name: 'Starting XI'},
        {id: 2, name: 'Substitutes'}
      ],
      sportSpecificName: 'Selection'
    },
    formations: {
      enabled: true,
      label: 'Formation',
      defaultFormation: '4-4-2'
    },
    showSpecialTeams: false,
    print: {
      stylesheets: ['/src/styles/print/base-print.css'],
      layout: 'standard'
    }
  },
  
  Volleyball: {
    tabs: [
      { id: 'Team', label: 'Team' }
    ],
    depthStrings: {
      defaultLabels: [
        {id: 1, name: 'Starters'},
        {id: 2, name: 'Rotation'}
      ],
      sportSpecificName: 'Rotation'
    },
    formations: {
      enabled: false
    },
    showSpecialTeams: false,
    print: {
      stylesheets: ['/src/styles/print/base-print.css'],
      layout: 'standard'
    }
  },
  
  Hockey: {
    tabs: [
      { id: 'Team', label: 'Team' }
    ],
    depthStrings: {
      defaultLabels: [
        {id: 1, name: '1st Line'},
        {id: 2, name: '2nd Line'},
        {id: 3, name: '3rd Line'},
        {id: 4, name: '4th Line'}
      ],
      sportSpecificName: 'Lines'
    },
    formations: {
      enabled: false
    },
    showSpecialTeams: false,
    print: {
      stylesheets: ['/src/styles/print/base-print.css'],
      layout: 'standard'
    }
  },
  
  Lacrosse: {
    tabs: [
      { id: 'Offense', label: 'Offense' },
      { id: 'Defense', label: 'Defense' },
      { id: 'Midfield', label: 'Midfield' }
    ],
    depthStrings: {
      defaultLabels: [
        {id: 1, name: '1st Line'},
        {id: 2, name: '2nd Line'}
      ],
      sportSpecificName: 'Lines'
    },
    formations: {
      enabled: false
    },
    showSpecialTeams: false,
    print: {
      stylesheets: ['/src/styles/print/base-print.css'],
      layout: 'standard'
    }
  }
};

// Helper function to get default depth strings for a sport
export function getDefaultDepthStrings(sport: Sport): {id: number; name: string; active: boolean}[] {
  const sportConfig = SPORT_RULESETS[sport];
  return sportConfig.depthStrings.defaultLabels.map(label => ({
    ...label,
    active: true
  }));
}

// Helper function to initialize depth strings for all tabs
export function initializeDepthStrings(sport: Sport): Record<string, {id: number; name: string; active: boolean}[]> {
  const sportConfig = SPORT_RULESETS[sport];
  const result: Record<string, {id: number; name: string; active: boolean}[]> = {};
  
  sportConfig.tabs.forEach(tab => {
    result[tab.id] = getDefaultDepthStrings(sport);
  });
  
  return result;
}

// Helper to determine if a sport uses offense/defense tabs
export function usesOffenseDefenseTabs(sport: Sport): boolean {
  const tabs = SPORT_RULESETS[sport].tabs;
  return tabs.some(tab => tab.id === 'Offense') && tabs.some(tab => tab.id === 'Defense');
} 